import React, { useState, useEffect, useMemo } from "react";
import {
  Select, Button, Card, Tag, Spin, Empty, Modal, Form, DatePicker, Checkbox, Input,
  message, Popconfirm, Avatar, Divider,
} from "antd";
import { UserOutlined, PlusOutlined, ClockCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { API_DOMAIN } from "../../constants";
import { useAuth } from "../../contexts/AuthContext";

dayjs.extend(utc);

export function AssignmentsPanel({ users }) {
  const { authState } = useAuth();
  const adminUserId = authState?.id;
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);
  const [levels, setLevels] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [power, setPower] = useState(null);
  const [powerLoading, setPowerLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [permanent, setPermanent] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    axios.get(`${API_DOMAIN}/approval-levels`, head)
      .then(r => setLevels(r.data?.data ?? r.data ?? []))
      .catch(() => {});
  }, []); // eslint-disable-line

  const loadPower = async (userId) => {
    if (!userId) { setPower(null); return; }
    setPowerLoading(true);
    try {
      const res = await axios.get(`${API_DOMAIN}/users/${userId}/approval-power`, { params: { userId: adminUserId }, ...head });
      setPower(res.data?.data ?? res.data ?? null);
    } catch { message.error("Failed to load user power"); }
    finally { setPowerLoading(false); }
  };

  const handleUserChange = (id) => {
    setSelectedUserId(id);
    loadPower(id);
  };

  const openAssignModal = () => {
    form.resetFields();
    form.setFieldsValue({ valid_from: dayjs() });
    setPermanent(true);
    setModalOpen(true);
  };

  const handleAssign = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const body = {
        approval_level_id: values.approval_level_id,
        valid_from: values.valid_from ? values.valid_from.utc().format("YYYY-MM-DD HH:mm:ss") : undefined,
        valid_until: !permanent && values.valid_until ? values.valid_until.utc().format("YYYY-MM-DD HH:mm:ss") : null,
        reason: values.reason || null,
        userId: adminUserId,
      };
      await axios.post(`${API_DOMAIN}/users/${selectedUserId}/approval-levels`, body, head);
      message.success("Approval power assigned");
      setModalOpen(false);
      loadPower(selectedUserId);
    } catch (e) {
      message.error(e?.response?.data?.message || "Assignment failed");
    } finally { setSaving(false); }
  };

  const handleDeactivate = async () => {
    if (!power?.assignment?.id) return;
    setDeactivating(true);
    try {
      await axios.patch(`${API_DOMAIN}/user-approval-levels/${power.assignment.id}`, { is_active: 0, userId: adminUserId }, head);
      message.success("Assignment deactivated");
      loadPower(selectedUserId);
    } catch (e) {
      message.error(e?.response?.data?.message || "Deactivation failed");
    } finally { setDeactivating(false); }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);
  const hasPower = power && power.priority > 0;

  const userOptions = users.map(u => ({
    value: u.id,
    label: u.name || u.username,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Select
        showSearch
        placeholder="Search and select a user..."
        options={userOptions}
        value={selectedUserId}
        onChange={handleUserChange}
        filterOption={(input, opt) => (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())}
        style={{ width: "100%" }}
        allowClear
        onClear={() => { setSelectedUserId(null); setPower(null); }}
      />

      {selectedUserId && (
        powerLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spin /></div>
        ) : (
          <Card
            size="small"
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Avatar size={28} icon={<UserOutlined />} style={{ backgroundColor: "#1677ff" }} />
                <span>{selectedUser?.name || selectedUser?.username || `User #${selectedUserId}`}</span>
              </div>
            }
            extra={
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAssignModal}>
                Assign Power
              </Button>
            }
          >
            {hasPower ? (
              <>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ color: "#8c8c8c", fontSize: 12 }}>Current Power: </span>
                  <Tag color="purple" style={{ fontWeight: 600 }}>
                    {power.level?.name} (Priority {power.priority})
                  </Tag>
                </div>
                {power.assignment?.valid_until && (
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    Valid until:{" "}
                    <strong style={{ color: "#262626" }}>
                      {dayjs.utc(power.assignment.valid_until.replace(" ", "T")).format("DD MMM YYYY · hh:mm A")}
                    </strong>
                  </div>
                )}
                {!power.assignment?.valid_until && (
                  <div style={{ fontSize: 12, color: "#52c41a", marginBottom: 8 }}>Permanent assignment</div>
                )}
                {power.assignment?.reason && (
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
                    Reason: {power.assignment.reason}
                  </div>
                )}
                {power.assignment?.id && (
                  <>
                    <Divider style={{ margin: "8px 0" }} />
                    <Popconfirm
                      title="Deactivate this assignment?"
                      description="The user will lose this approval power immediately."
                      onConfirm={handleDeactivate}
                      okText="Deactivate" okButtonProps={{ danger: true }}
                      cancelText="Cancel"
                    >
                      <Button danger size="small" loading={deactivating}>Deactivate</Button>
                    </Popconfirm>
                  </>
                )}
              </>
            ) : (
              <Empty description="No approval power assigned" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        )
      )}

      {!selectedUserId && (
        <Empty description="Select a user to manage their approval power" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}

      <Modal
        open={modalOpen}
        title="Assign Approval Power"
        onCancel={() => setModalOpen(false)}
        onOk={handleAssign}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="approval_level_id" label="Approval Level" rules={[{ required: true }]}>
            <Select placeholder="Select a level">
              {levels.filter(l => l.is_active).sort((a, b) => a.priority - b.priority).map(l => (
                <Select.Option key={l.id} value={l.id}>
                  {l.name} — Priority {l.priority}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="valid_from" label="Valid From" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY HH:mm" />
          </Form.Item>
          <Form.Item label="Duration">
            <Checkbox checked={permanent} onChange={e => setPermanent(e.target.checked)}>
              Permanent (no expiry)
            </Checkbox>
          </Form.Item>
          {!permanent && (
            <Form.Item name="valid_until" label="Valid Until" rules={[{ required: true, message: "Required for temporary assignment" }]}>
              <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY HH:mm" />
            </Form.Item>
          )}
          <Form.Item name="reason" label="Reason (optional)">
            <Input.TextArea rows={2} placeholder="e.g. Covering for manager on leave" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
